import React, { useState, useEffect } from 'react';
import { User, Calendar, Tag, Phone, Mail, BookmarkCheck, UserCheck } from 'lucide-react';
import FormInput from '../FormInput';

const EnseignantForm = ({ 
  initialData = null, 
  statusEnseignants = [], 
  onSubmit, 
  submitting = false,
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    genre: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse: '',
    telephone: '',
    email: '',
    grade: '',
    status: '',
    matricule: ''
  });

  const [errors, setErrors] = useState({});

  // Initialiser le formulaire avec les données existantes
  useEffect(() => {
    if (initialData) {
      setFormData({
        nom: initialData.nom || '',
        prenom: initialData.prenom || '',
        genre: initialData.genre || '',
        date_naissance: initialData.date_naissance || '',
        lieu_naissance: initialData.lieu_naissance || '',
        adresse: initialData.adresse || '',
        telephone: initialData.telephone || '',
        email: initialData.email || '',
        grade: initialData.grade || '',
        status: initialData.status?.id || '',
        matricule: initialData.matricule || ''
      });
    } else {
      setFormData({
        nom: '',
        prenom: '',
        genre: '',
        date_naissance: '',
        lieu_naissance: '',
        adresse: '',
        telephone: '',
        email: '',
        grade: '',
        status: '',
        matricule: ''
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    }
    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est requis";
    }
    if (!formData.genre) {
      newErrors.genre = "Le genre est requis";
    }
    if (!formData.date_naissance) {
      newErrors.date_naissance = "La date de naissance est requise";
    }
    if (!formData.lieu_naissance.trim()) {
      newErrors.lieu_naissance = "Le lieu de naissance est requis";
    }
    if (!formData.adresse.trim()) {
      newErrors.adresse = "L'adresse est requise";
    }
    if (!formData.telephone.trim()) {
      newErrors.telephone = "Le téléphone est requis";
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email est invalide";
    }
    if (!formData.grade.trim()) {
      newErrors.grade = "Le grade est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-5xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormInput
          label="Prénom"
          type="text"
          name="prenom"
          value={formData.prenom}
          onChange={handleInputChange}
          placeholder="Prénom de l'enseignant"
          required
          error={errors.prenom}
          icon={User}
        />
        
        <FormInput
          label="Nom"
          type="text"
          name="nom"
          value={formData.nom}
          onChange={handleInputChange}
          placeholder="Nom de l'enseignant"
          required
          error={errors.nom}
          icon={User}
        />

        <FormInput
          label="Genre"
          type="select"
          name="genre"
          value={formData.genre}
          onChange={handleInputChange}
          options={[
            { value: '', label: 'Sélectionner un genre' },
            { value: 'M', label: 'Masculin' },
            { value: 'F', label: 'Féminin' }
          ]}
          required
          error={errors.genre}
          icon={User}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormInput
          label="Date de naissance"
          type="date"
          name="date_naissance"
          value={formData.date_naissance}
          onChange={handleInputChange}
          required
          error={errors.date_naissance}
          icon={Calendar}
        />
        
        <FormInput
          label="Lieu de naissance"
          type="text"
          name="lieu_naissance"
          value={formData.lieu_naissance}
          onChange={handleInputChange}
          placeholder="Lieu de naissance"
          required
          error={errors.lieu_naissance}
          icon={Tag}
        />
        
        <FormInput
          label="Matricule"
          type="text"
          name="matricule"
          value={formData.matricule}
          onChange={handleInputChange}
          placeholder="Numéro matricule (optionnel)"
          error={errors.matricule}
          icon={Tag}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormInput
          label="Téléphone"
          type="text"
          name="telephone"
          value={formData.telephone}
          onChange={handleInputChange}
          placeholder="Numéro de téléphone"
          required
          error={errors.telephone}
          icon={Phone}
        />
        
        <FormInput
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Adresse email"
          required
          error={errors.email}
          icon={Mail}
        />
        
        <FormInput
          label="Grade"
          type="text"
          name="grade"
          value={formData.grade}
          onChange={handleInputChange}
          placeholder="Grade de l'enseignant"
          required
          error={errors.grade}
          icon={BookmarkCheck}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormInput
          label="Adresse"
          type="text"
          name="adresse"
          value={formData.adresse}
          onChange={handleInputChange}
          placeholder="Adresse de résidence"
          required
          error={errors.adresse}
          icon={Tag}
        />
        
        <FormInput
          label="Statut"
          type="select"
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          options={[
            { value: '', label: 'Sélectionner un statut' },
            ...(Array.isArray(statusEnseignants) ? statusEnseignants.map(status => ({ 
              value: status.id, 
              label: status.libelle 
            })) : [])
          ]}
          error={errors.status}
          icon={UserCheck}
        />
      </div>

      <div className="text-sm text-gray-500">
        <span className="text-red-500">*</span> Champs obligatoires
      </div>

      <div className="flex justify-end space-x-4 mt-4">
        <button
          type="button"
          className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={onCancel}
        >
          Annuler
        </button>
        <button
          type="submit"
          className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Traitement...
            </>
          ) : (
            initialData ? 'Mettre à jour' : 'Créer'
          )}
        </button>
      </div>
    </form>
  );
};

export default EnseignantForm; 